import { getSupabase } from "../components/supabaseClient";
import { cortiLogger } from "./cortiLogger";

// Global credit tracking utility for current session
let liveCreditUsage = 0;
let recordedCreditUsage = 0;
let codesCreditUsage = 0;

// Function to get current user email
const getCurrentUserEmail = async () => {
  try {
    // console.log("💳 Credit tracker: Getting current user email...");
    const supabase = await getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const userEmail = session?.user?.email;
    // console.log("💳 Credit tracker: User email retrieved:", userEmail);
    return userEmail;
  } catch (error) {
    // console.error("💳 Credit tracker: Error getting current user email:", error);
    // console.error("💳 Credit tracker: Error stack:", error.stack);
    return null;
  }
};

// Function to get current user ID
const getCurrentUserId = async () => {
  try {
    // console.log("💳 Credit tracker: Getting current user ID...");
    const supabase = await getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    // console.log("💳 Credit tracker: User ID retrieved:", userId);
    return userId;
  } catch (error) {
    // console.error("💳 Credit tracker: Error getting current user ID:", error);
    // console.error("💳 Credit tracker: Error stack:", error.stack);
    return null;
  }
};

// Function to load user's total credit usage from database
export const loadUserCreditUsage = async () => {
  try {
    // console.log("💳 Credit tracker: Starting loadUserCreditUsage...");

    const supabase = await getSupabase();
    // console.log("💳 Credit tracker: Supabase client obtained");

    const userEmail = await getCurrentUserEmail();
    // console.log("💳 Credit tracker: User email retrieved:", userEmail);

    if (!userEmail) {
      // console.log("💳 Credit tracker: No user email found, skipping database load");
      return { liveCredits: 0, recordedCredits: 0, codesCredits: 0 };
    }

    // console.log("💳 Credit tracker: Loading credit usage for user:", userEmail);

    // Calculate actual totals from corti_logs table for accurate credit tracking
    // console.log("💳 Credit tracker: Querying corti_logs table...");
    const { data: logsData, error: logsError } = await supabase
      .from('corti_logs')
      .select('transcription_type, credit_usage')
      .eq('user_email', userEmail);

    // console.log("💳 Credit tracker: Query completed. Data:", logsData, "Error:", logsError);

    if (logsError) {
      // console.error("💳 Credit tracker: Error loading corti_logs:", logsError);
      // console.error("💳 Credit tracker: Error details:", {
      //   code: logsError.code,
      //   message: logsError.message,
      //   details: logsError.details,
      //   hint: logsError.hint
      // });
      // Fallback to old system
      return await loadUserCreditUsageFromOldSystem();
    }

    // Calculate totals from logs
    let liveCredits = 0;
    let recordedCredits = 0;
    let codesCredits = 0;
    
    if (logsData && logsData.length > 0) {
      // console.log("💳 Credit tracker: Processing", logsData.length, "log entries");
      logsData.forEach((log, index) => {
        // console.log(`💳 Credit tracker: Processing log ${index + 1}:`, log);
        if (log.transcription_type === 'live') {
          liveCredits += log.credit_usage || 0;
        } else if (log.transcription_type === 'recorded') {
          recordedCredits += log.credit_usage || 0;
        } else if (log.transcription_type === 'codes') {
          codesCredits += log.credit_usage || 0;
        }
      });
    } else {
      // console.log("💳 Credit tracker: No log entries found for user");
    }
    
    // Load codes credits from users table (codes are tracked separately in users.codes_credit_usage)
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('codes_credit_usage')
        .eq('email', userEmail)
        .single();
      
      if (!userError && userData) {
        codesCredits = userData.codes_credit_usage || 0;
        // console.log("💳 Credit tracker: Loaded codes credits from users table:", codesCredits);
      }
    } catch (error) {
      // console.error("💳 Credit tracker: Error loading codes credits from users table:", error);
    }
    
    // console.log("💳 Credit tracker: Calculated from corti_logs - Live:", liveCredits, "Recorded:", recordedCredits, "Codes:", codesCredits);
    // console.log("💳 Credit tracker: Total logs processed:", logsData?.length || 0);
    
    // Update global variables
    liveCreditUsage = liveCredits;
    recordedCreditUsage = recordedCredits;
    codesCreditUsage = codesCredits;
    
    return { liveCredits, recordedCredits, codesCredits };
  } catch (error) {
    // console.error("💳 Credit tracker: Exception in loadUserCreditUsage:", error);
    // console.error("💳 Credit tracker: Error stack:", error.stack);
    // Fallback to old system
    return await loadUserCreditUsageFromOldSystem();
  }
};

// Fallback function to load from old system
const loadUserCreditUsageFromOldSystem = async () => {
  try {
    // console.log("💳 Credit tracker: Starting fallback to old system...");

    const supabase = await getSupabase();
    // console.log("💳 Credit tracker: Supabase client obtained for fallback");

    const userEmail = await getCurrentUserEmail();
    // console.log("💳 Credit tracker: Falling back to old system for user:", userEmail);

    // console.log("💳 Credit tracker: Querying users table for old credit system...");
    const { data, error } = await supabase
      .from('users')
      .select('live_credit_usage, recorded_credit_usage, codes_credit_usage')
      .eq('email', userEmail)
      .single();

    // console.log("💳 Credit tracker: Old system query completed. Data:", data, "Error:", error);

    if (error) {
      // console.error("💳 Credit tracker: Error in old system query:", error);
      // console.error("💳 Credit tracker: Error details:", {
      //   code: error.code,
      //   message: error.message,
      //   details: error.details,
      //   hint: error.hint
      // });
      
      if (error.code === 'PGRST116') {
        // console.log("💳 Credit tracker: User not found in database, initializing with 0 credits");
        return { liveCredits: 0, recordedCredits: 0, codesCredits: 0 };
      }
      throw error;
    }

    const liveCredits = data?.live_credit_usage || 0;
    const recordedCredits = data?.recorded_credit_usage || 0;
    const codesCredits = data?.codes_credit_usage || 0;
    
    // console.log("💳 Credit tracker: Loaded from old system - Live:", liveCredits, "Recorded:", recordedCredits, "Codes:", codesCredits);
    
    // Update global variables
    liveCreditUsage = liveCredits;
    recordedCreditUsage = recordedCredits;
    codesCreditUsage = codesCredits;
    
    return { liveCredits, recordedCredits, codesCredits };
  } catch (error) {
    // console.error("💳 Credit tracker: Error in fallback system:", error);
    return { liveCredits: 0, recordedCredits: 0, codesCredits: 0 };
  }
};

// Function to save user's total credit usage to database
const saveUserCreditUsage = async (liveCredits, recordedCredits, codesCredits = null) => {
  try {
    const supabase = await getSupabase();
    const userEmail = await getCurrentUserEmail();
    
    if (!userEmail) {
      // console.log("💳 Credit tracker: No user email found, skipping database save");
      return;
    }

    // console.log("💳 Credit tracker: Saving credit usage to database for user:", userEmail);
    // console.log("💳 Credit tracker: Live credits:", liveCredits, "Recorded credits:", recordedCredits, "Codes credits:", codesCredits);
    
    // Build update object
    const updateData = { 
      live_credit_usage: liveCredits,
      recorded_credit_usage: recordedCredits,
      last_credit_update: new Date().toISOString()
    };
    
    // Only update codes_credit_usage if provided
    if (codesCredits !== null) {
      updateData.codes_credit_usage = codesCredits;
    }
    
    // Update user's credit usage in the users table
    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('email', userEmail);

    if (error) {
      // console.error("💳 Credit tracker: Error saving credit usage:", error);
      throw error;
    }

    // console.log("💳 Credit tracker: Successfully saved credit usage to database");
  } catch (error) {
    // console.error("💳 Credit tracker: Error in saveUserCreditUsage:", error);
  }
};

// Function to add live credits used (for current session)
export const addLiveCredits = async (creditsUsed) => {
  try {
    // console.log("💳 Live credit tracker: Starting addLiveCredits...");
    // console.log("💳 Live credit tracker: addLiveCredits called with:", creditsUsed);
    // console.log("💳 Live credit tracker: Previous total:", liveCreditUsage);

    liveCreditUsage += creditsUsed;
    // console.log("💳 Live credit tracker: Added", creditsUsed, "credits. New total:", liveCreditUsage);

    // Always save to the old system to maintain separate live/recorded tracking
    // console.log("💳 Live credit tracker: Saving to old system...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Also update the new system for monthly tracking
    // console.log("💳 Live credit tracker: Updating new system...");
    try {
      const result = await cortiLogger.updateUserCredits(creditsUsed);
      if (result && result.success) {
        // console.log("💳 Live credit tracker: Updated credits in new system:", result);
        
        // Check if user would exceed monthly limit
        if (result.wouldExceedMonthly) {
          // console.warn("💳 Live credit tracker: User would exceed monthly credit limit:", {
          //   monthly: result.wouldExceedMonthly
          // });
        }
      }
    } catch (error) {
      // console.error("💳 Live credit tracker: Error updating credits in new system:", error);
      // console.error("💳 Live credit tracker: Error stack:", error.stack);
    }
    
    // Dispatch event to notify components
    // console.log("💳 Live credit tracker: Dispatching liveCreditsUpdated event with totalLiveCredits:", liveCreditUsage);
    window.dispatchEvent(new CustomEvent('liveCreditsUpdated', {
      detail: { totalLiveCredits: liveCreditUsage }
    }));
    // console.log("💳 Live credit tracker: Event dispatched successfully");

    // console.log("💳 Live credit tracker: addLiveCredits completed successfully");
  } catch (error) {
    // console.error("💳 Live credit tracker: Error in addLiveCredits:", error);
    // console.error("💳 Live credit tracker: Error stack:", error.stack);
  }
};

// Function to add recorded credits used (for current session)
export const addRecordedCredits = async (creditsUsed) => {
  try {
    // console.log("💳 Recorded credit tracker: Starting addRecordedCredits...");
    // console.log("💳 Recorded credit tracker: addRecordedCredits called with:", creditsUsed);
    // console.log("💳 Recorded credit tracker: Previous total:", recordedCreditUsage);

    recordedCreditUsage += creditsUsed;
    // console.log("💳 Recorded credit tracker: Added", creditsUsed, "credits. New total:", recordedCreditUsage);

    // Always save to the old system to maintain separate live/recorded tracking
    // console.log("💳 Recorded credit tracker: Saving to old system...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Also update the new system for monthly tracking
    // console.log("💳 Recorded credit tracker: Updating new system...");
    try {
      const result = await cortiLogger.updateUserCredits(creditsUsed);
      if (result && result.success) {
        // console.log("💳 Recorded credit tracker: Updated credits in new system:", result);
        
        // Check if user would exceed monthly limit
        if (result.wouldExceedMonthly) {
          // console.warn("💳 Recorded credit tracker: User would exceed monthly credit limit:", {
          //   monthly: result.wouldExceedMonthly
          // });
        }
      }
    } catch (error) {
      // console.error("💳 Recorded credit tracker: Error updating credits in new system:", error);
      // console.error("💳 Recorded credit tracker: Error stack:", error.stack);
    }
    
    // Dispatch event to notify components
    // console.log("💳 Recorded credit tracker: Dispatching recordedCreditsUpdated event with totalRecordedCredits:", recordedCreditUsage);
    window.dispatchEvent(new CustomEvent('recordedCreditsUpdated', {
      detail: { totalRecordedCredits: recordedCreditUsage }
    }));
    // console.log("💳 Recorded credit tracker: Event dispatched successfully");

    // console.log("💳 Recorded credit tracker: addRecordedCredits completed successfully");
  } catch (error) {
    // console.error("💳 Recorded credit tracker: Error in addRecordedCredits:", error);
    // console.error("💳 Recorded credit tracker: Error stack:", error.stack);
  }
};

// Function to add codes credits used (for current session)
export const addCodesCredits = async (creditsUsed) => {
  try {
    // console.log("💳 Codes credit tracker: Starting addCodesCredits...");
    // console.log("💳 Codes credit tracker: addCodesCredits called with:", creditsUsed);
    // console.log("💳 Codes credit tracker: Previous total:", codesCreditUsage);

    codesCreditUsage += creditsUsed;
    // console.log("💳 Codes credit tracker: Added", creditsUsed, "credits. New total:", codesCreditUsage);

    // Always save to the old system to maintain separate codes tracking
    // console.log("💳 Codes credit tracker: Saving to old system...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Also update the new system for monthly tracking
    // console.log("💳 Codes credit tracker: Updating new system...");
    try {
      const result = await cortiLogger.updateUserCredits(creditsUsed);
      if (result && result.success) {
        // console.log("💳 Codes credit tracker: Updated credits in new system:", result);
        
        // Check if user would exceed monthly limit
        if (result.wouldExceedMonthly) {
          // console.warn("💳 Codes credit tracker: User would exceed monthly credit limit:", {
          //   monthly: result.wouldExceedMonthly
          // });
        }
      }
    } catch (error) {
      // console.error("💳 Codes credit tracker: Error updating credits in new system:", error);
      // console.error("💳 Codes credit tracker: Error stack:", error.stack);
    }
    
    // Dispatch event to notify components
    // console.log("💳 Codes credit tracker: Dispatching codesCreditsUpdated event with totalCodesCredits:", codesCreditUsage);
    window.dispatchEvent(new CustomEvent('codesCreditsUpdated', {
      detail: { totalCodesCredits: codesCreditUsage }
    }));
    // console.log("💳 Codes credit tracker: Event dispatched successfully");

    // console.log("💳 Codes credit tracker: addCodesCredits completed successfully");
  } catch (error) {
    // console.error("💳 Codes credit tracker: Error in addCodesCredits:", error);
    // console.error("💳 Codes credit tracker: Error stack:", error.stack);
  }
};

// Function to get current live credit usage
export const getLiveCreditUsage = () => {
  // console.log("💳 Live credit tracker: getLiveCreditUsage called, returning:", liveCreditUsage);
  return liveCreditUsage;
};

// Function to get current recorded credit usage
export const getRecordedCreditUsage = () => {
  // console.log("💳 Recorded credit tracker: getRecordedCreditUsage called, returning:", recordedCreditUsage);
  return recordedCreditUsage;
};

// Function to get current codes credit usage
export const getCodesCreditUsage = () => {
  // console.log("💳 Codes credit tracker: getCodesCreditUsage called, returning:", codesCreditUsage);
  return codesCreditUsage;
};

// Function to get total credit usage
export const getTotalCreditUsage = () => {
  const total = liveCreditUsage + recordedCreditUsage + codesCreditUsage;
  // console.log("💳 Credit tracker: getTotalCreditUsage called, returning:", total);
  return total;
};

// Function to reset live credit usage
export const resetLiveCreditUsage = async () => {
  try {
    // console.log("💳 Live credit tracker: Starting resetLiveCreditUsage...");

    liveCreditUsage = 0;
    // console.log("💳 Live credit tracker: Reset to 0");

    // Save to database
    // console.log("💳 Live credit tracker: Saving to database...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Dispatch event to notify components
    // console.log("💳 Live credit tracker: Dispatching liveCreditsUpdated event...");
    window.dispatchEvent(new CustomEvent('liveCreditsUpdated', {
      detail: { totalLiveCredits: liveCreditUsage }
    }));

    // console.log("💳 Live credit tracker: Reset completed successfully");
  } catch (error) {
    // console.error("💳 Live credit tracker: Error in resetLiveCreditUsage:", error);
    // console.error("💳 Live credit tracker: Error stack:", error.stack);
  }
};

// Function to reset recorded credit usage
export const resetRecordedCreditUsage = async () => {
  try {
    // console.log("💳 Recorded credit tracker: Starting resetRecordedCreditUsage...");

    recordedCreditUsage = 0;
    // console.log("💳 Recorded credit tracker: Reset to 0");

    // Save to database
    // console.log("💳 Recorded credit tracker: Saving to database...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Dispatch event to notify components
    // console.log("💳 Recorded credit tracker: Dispatching recordedCreditsUpdated event...");
    window.dispatchEvent(new CustomEvent('recordedCreditsUpdated', {
      detail: { totalRecordedCredits: recordedCreditUsage }
    }));

    // console.log("💳 Recorded credit tracker: Reset completed successfully");
  } catch (error) {
    // console.error("💳 Recorded credit tracker: Error in resetRecordedCreditUsage:", error);
    // console.error("💳 Recorded credit tracker: Error stack:", error.stack);
  }
};

// Function to set live credit usage to a specific value
export const setLiveCreditUsage = async (value) => {
  try {
    // console.log("💳 Live credit tracker: Starting setLiveCreditUsage...");
    // console.log("💳 Live credit tracker: Setting to value:", value);

    liveCreditUsage = value;
    // console.log("💳 Live credit tracker: Set to", value);

    // Save to database
    // console.log("💳 Live credit tracker: Saving to database...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Dispatch event to notify components
    // console.log("💳 Live credit tracker: Dispatching liveCreditsUpdated event...");
    window.dispatchEvent(new CustomEvent('liveCreditsUpdated', {
      detail: { totalLiveCredits: liveCreditUsage }
    }));

    // console.log("💳 Live credit tracker: Set completed successfully");
  } catch (error) {
    // console.error("💳 Live credit tracker: Error in setLiveCreditUsage:", error);
    // console.error("💳 Live credit tracker: Error stack:", error.stack);
  }
};

// Function to set recorded credit usage to a specific value
export const setRecordedCreditUsage = async (value) => {
  try {
    // console.log("💳 Recorded credit tracker: Starting setRecordedCreditUsage...");
    // console.log("💳 Recorded credit tracker: Setting to value:", value);

    recordedCreditUsage = value;
    // console.log("💳 Recorded credit tracker: Set to", value);

    // Save to database
    // console.log("💳 Recorded credit tracker: Saving to database...");
    await saveUserCreditUsage(liveCreditUsage, recordedCreditUsage, codesCreditUsage);

    // Dispatch event to notify components
    // console.log("💳 Recorded credit tracker: Dispatching recordedCreditsUpdated event...");
    window.dispatchEvent(new CustomEvent('recordedCreditsUpdated', {
      detail: { totalRecordedCredits: recordedCreditUsage }
    }));

    // console.log("💳 Recorded credit tracker: Set completed successfully");
  } catch (error) {
    // console.error("💳 Recorded credit tracker: Error in setRecordedCreditUsage:", error);
    // console.error("💳 Recorded credit tracker: Error stack:", error.stack);
  }
};

// Function to initialize credit tracking (call this when app starts)
export const initializeCreditTracking = async () => {
  try {
    // console.log("💳 Credit tracker: Starting initializeCreditTracking...");

    // console.log("💳 Credit tracker: Initializing credit tracking");
    await loadUserCreditUsage();

    // Dispatch initial events
    // console.log("💳 Credit tracker: Dispatching initial events...");
    window.dispatchEvent(new CustomEvent('liveCreditsUpdated', {
      detail: { totalLiveCredits: liveCreditUsage }
    }));
    window.dispatchEvent(new CustomEvent('recordedCreditsUpdated', {
      detail: { totalRecordedCredits: recordedCreditUsage }
    }));
    window.dispatchEvent(new CustomEvent('codesCreditsUpdated', {
      detail: { totalCodesCredits: codesCreditUsage }
    }));
    
    // console.log("💳 Credit tracker: Initialization complete - Live:", liveCreditUsage, "Recorded:", recordedCreditUsage, "Codes:", codesCreditUsage);
    // console.log("💳 Credit tracker: Initialization completed successfully");
  } catch (error) {
    // console.error("💳 Credit tracker: Error in initializeCreditTracking:", error);
    // console.error("💳 Credit tracker: Error stack:", error.stack);
  }
};
